const asArray = (value) => (Array.isArray(value) ? value : [])

const asNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getCollection = (payload, key) => {
  if (Array.isArray(payload?.[key])) return payload[key]
  if (Array.isArray(payload?.[key]?.[key])) return payload[key][key]
  return []
}

const cleanName = (value) =>
  typeof value === "string" ? value.trim() : ""

const conditionEventName = (conditions) => {
  for (const condition of asArray(conditions)) {
    const parameter = cleanName(condition?.eventParameter)
    if (parameter !== "event_name") continue
    const value =
      condition?.stringValue ??
      condition?.comparisonValue ??
      condition?.value ??
      ""
    const normalized = cleanName(String(value))
    if (normalized) return normalized
  }
  return ""
}

const mutationNames = (mutations) =>
  asArray(mutations)
    .map((mutation) => cleanName(mutation?.parameter))
    .filter(Boolean)

const reportHeaderIndex = (headers, name) =>
  asArray(headers).findIndex((header) => header?.name === name)

export const normalizeObservedEvents = (report = {}) => {
  const dimensionIndex = reportHeaderIndex(report.dimensionHeaders, "eventName")
  const metricIndexes = {
    eventCount: reportHeaderIndex(report.metricHeaders, "eventCount"),
    totalUsers: reportHeaderIndex(report.metricHeaders, "totalUsers"),
    keyEvents: reportHeaderIndex(report.metricHeaders, "keyEvents"),
  }

  return asArray(report.rows)
    .map((row) => {
      const eventName = cleanName(
        row?.dimensionValues?.[dimensionIndex < 0 ? 0 : dimensionIndex]?.value,
      )
      if (!eventName) return null
      const metric = (name) => {
        const index = metricIndexes[name]
        return index < 0 ? 0 : asNumber(row?.metricValues?.[index]?.value)
      }
      return {
        eventName,
        eventCount: metric("eventCount"),
        totalUsers: metric("totalUsers"),
        keyEvents: metric("keyEvents"),
      }
    })
    .filter(Boolean)
}

export const buildEventInventory = ({
  eventReport = {},
  keyEventData = {},
  taggingEvents = [],
} = {}) => {
  const inventory = new Map()
  const ensure = (eventName) => {
    const name = cleanName(eventName)
    if (!name) return null
    if (!inventory.has(name)) {
      inventory.set(name, {
        eventName: name,
        eventCount: 0,
        totalUsers: 0,
        keyEventCount: 0,
        isKeyEvent: false,
        observedInGa4: false,
        configuredInGtm: false,
        gtmTagId: null,
        gtmTriggerIds: [],
        parameters: [],
        ruleSources: [],
      })
    }
    return inventory.get(name)
  }

  for (const event of normalizeObservedEvents(eventReport)) {
    Object.assign(ensure(event.eventName), {
      eventCount: event.eventCount,
      totalUsers: event.totalUsers,
      keyEventCount: event.keyEvents,
      observedInGa4: true,
    })
  }

  for (const keyEvent of getCollection(keyEventData, "conversionEvents")) {
    const row = ensure(keyEvent?.eventName)
    if (row) row.isKeyEvent = true
  }

  for (const taggingEvent of asArray(taggingEvents)) {
    const row = ensure(taggingEvent?.ga4_event_name || taggingEvent?.eventName)
    if (!row) continue
    row.configuredInGtm = Boolean(
      taggingEvent?.configured_in_gtm ?? taggingEvent?.configuredInGtm,
    )
    row.gtmTagId =
      taggingEvent?.source_tag_id || taggingEvent?.sourceTagId || null
    row.gtmTriggerIds = Array.from(
      new Set([
        ...row.gtmTriggerIds,
        ...asArray(taggingEvent?.trigger_ids || taggingEvent?.triggerIds).map(String),
      ]),
    )
    row.parameters = Array.from(
      new Set([
        ...row.parameters,
        ...Object.keys(
          taggingEvent?.ga4_parameter_mappings ||
            taggingEvent?.ga4ParameterMappings ||
            {},
        ),
      ]),
    ).sort()
  }

  for (const rule of asArray(keyEventData?.eventCreateRules)) {
    const sourceEvent = conditionEventName(rule?.matchingConditions)
    const destinationEvent = cleanName(rule?.destinationEvent)
    const names = mutationNames(rule?.parameterMutations)
    for (const eventName of [sourceEvent, destinationEvent]) {
      const row = ensure(eventName)
      if (!row) continue
      row.parameters = Array.from(new Set([...row.parameters, ...names])).sort()
      row.ruleSources.push(
        eventName === destinationEvent ? "GA4 create rule" : "GA4 source rule",
      )
    }
  }

  for (const rule of asArray(keyEventData?.eventEditRules)) {
    const eventName = cleanName(rule?.eventName) || conditionEventName(rule?.matchingConditions)
    const row = ensure(eventName)
    if (!row) continue
    row.parameters = Array.from(
      new Set([...row.parameters, ...mutationNames(rule?.parameterMutations)]),
    ).sort()
    row.ruleSources.push("GA4 modify rule")
  }

  return Array.from(inventory.values()).sort((left, right) => {
    if (right.eventCount !== left.eventCount) return right.eventCount - left.eventCount
    return left.eventName.localeCompare(right.eventName)
  })
}

export const buildParameterInventory = ({
  extendedAudit = {},
  keyEventData = {},
  taggingEvents = [],
} = {}) => {
  const parameters = new Map()
  const add = (name, value = {}) => {
    const parameterName = cleanName(name)
    if (!parameterName) return
    const current = parameters.get(parameterName) || {
      parameterName,
      displayNames: [],
      scopes: [],
      types: [],
      sources: [],
      events: [],
      gtmValues: [],
    }
    const merge = (key, entries) => {
      current[key] = Array.from(
        new Set([...current[key], ...asArray(entries).map(cleanName).filter(Boolean)]),
      ).sort()
    }
    merge("displayNames", value.displayNames)
    merge("scopes", value.scopes)
    merge("types", value.types)
    merge("sources", value.sources)
    merge("events", value.events)
    merge("gtmValues", value.gtmValues)
    parameters.set(parameterName, current)
  }

  for (const dimension of getCollection(extendedAudit, "customDimensions")) {
    add(dimension?.parameterName, {
      displayNames: [dimension?.displayName],
      scopes: [dimension?.scope],
      types: ["Dimension"],
      sources: ["GA4 registered definition"],
    })
  }

  for (const metric of getCollection(extendedAudit, "customMetrics")) {
    add(metric?.parameterName, {
      displayNames: [metric?.displayName],
      scopes: [metric?.scope],
      types: ["Metric"],
      sources: ["GA4 registered definition"],
    })
  }

  for (const event of asArray(taggingEvents)) {
    const eventName = cleanName(event?.ga4_event_name || event?.eventName)
    const mappings = event?.ga4_parameter_mappings || event?.ga4ParameterMappings || {}
    for (const [parameterName, source] of Object.entries(mappings)) {
      const gtmValue =
        typeof source === "string"
          ? source
          : source?.path || source?.constant || JSON.stringify(source)
      add(parameterName, {
        events: [eventName],
        sources: ["GTM event tag"],
        gtmValues: [gtmValue],
      })
    }
  }

  const addRuleParameters = (rule, ruleLabel, eventNames) => {
    for (const mutation of asArray(rule?.parameterMutations)) {
      const value =
        mutation?.copyFromParameter ||
        mutation?.parameterValue?.stringValue ||
        mutation?.parameterValue?.intValue ||
        mutation?.parameterValue?.doubleValue ||
        ""
      add(mutation?.parameter, {
        events: eventNames,
        sources: [ruleLabel],
        gtmValues: value === "" ? [] : [String(value)],
      })
    }
  }

  for (const rule of asArray(keyEventData?.eventCreateRules)) {
    addRuleParameters(rule, "GA4 create rule", [
      conditionEventName(rule?.matchingConditions),
      cleanName(rule?.destinationEvent),
    ])
  }
  for (const rule of asArray(keyEventData?.eventEditRules)) {
    addRuleParameters(rule, "GA4 modify rule", [
      cleanName(rule?.eventName) || conditionEventName(rule?.matchingConditions),
    ])
  }

  return Array.from(parameters.values()).sort((left, right) =>
    left.parameterName.localeCompare(right.parameterName),
  )
}

export const buildConfigurationFindings = ({
  extendedAudit = {},
  eventInventory = [],
  parameterInventory = [],
  hasGtmSetup = false,
} = {}) => {
  const findings = []
  const add = (severity, area, title, detail) =>
    findings.push({ severity, area, title, detail })
  const settings = extendedAudit?.settings || extendedAudit || {}
  const streams = getCollection(extendedAudit, "dataStreams")
  const webStreams = streams.filter(
    (stream) => stream?.type === "WEB_DATA_STREAM" || stream?.webStreamData,
  )

  if (!settings?.timeZone) {
    add("error", "Property", "Reporting time zone is missing", "Set the business reporting time zone before interpreting daily trends.")
  }
  if (!settings?.currencyCode) {
    add("error", "Property", "Currency is missing", "Set a currency so revenue and value metrics are reported consistently.")
  }
  if (!settings?.industryCategory || settings.industryCategory === "INDUSTRY_CATEGORY_UNSPECIFIED") {
    add("warning", "Property", "Industry category is unspecified", "Choose the closest industry category for more relevant benchmarks and recommendations.")
  }
  if (!webStreams.length) {
    add("error", "Collection", "No web data stream found", "Connect a web stream before configuring browser-side collection through GTM.")
  }
  for (const stream of webStreams) {
    const name = stream?.displayName || stream?.name || "Web stream"
    if (!stream?.webStreamData?.measurementId) {
      add("error", "Collection", `${name} has no measurement ID`, "GTM needs the stream measurement ID to send events to this property.")
    }
    if (!stream?.webStreamData?.defaultUri) {
      add("warning", "Collection", `${name} has no default URL`, "Add the production site URL to make the stream easier to validate and manage.")
    }
  }
  if (extendedAudit?.dataRetentionSettings?.eventDataRetention === "TWO_MONTHS") {
    add("warning", "Data retention", "Event data retention is two months", "Consider fourteen months when longer exploration and cohort analysis are required.")
  }
  if (!eventInventory.length) {
    add("error", "Events", "No events observed", "No event names were returned for the selected audit window.")
  }
  for (const event of eventInventory) {
    if (event.isKeyEvent && !event.observedInGa4) {
      add("error", "Key events", `${event.eventName} is not being observed`, "This event is marked as a key event but recorded no activity in the audit window.")
    } else if (event.configuredInGtm && !event.observedInGa4) {
      add("warning", "GTM", `${event.eventName} is configured but dormant`, "The GTM inventory includes this event, but GA4 recorded no activity in the audit window.")
    }
  }
  const registered = new Set(
    parameterInventory
      .filter((parameter) => parameter.sources.includes("GA4 registered definition"))
      .map((parameter) => parameter.parameterName),
  )
  for (const parameter of parameterInventory) {
    if (parameter.sources.includes("GTM event tag") && !registered.has(parameter.parameterName)) {
      add("warning", "Parameters", `${parameter.parameterName} is not registered`, "GTM sends this custom parameter, but it is not registered as a GA4 custom definition.")
    }
  }
  if (!hasGtmSetup) {
    add("info", "GTM", "No GTM setup is linked", "Link this GA4 property to a GTM container to audit event tags, triggers, and unregistered parameters.")
  }

  const rank = { error: 0, warning: 1, info: 2 }
  return findings.sort((left, right) => rank[left.severity] - rank[right.severity])
}

export const ga4AuditCollection = getCollection
